import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Edit3, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import { useTaal } from "@/contexts/TaalContext";
import BijlageKaart from "../components/BijlageKaart";


export default function ArtifactPage() {
  const { t } = useTaal();
  const { projectId, artifactId } = useParams();
  const navigate = useNavigate();
  const [artifact, setArtifact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editType, setEditType] = useState("text");

  const fetchArtifact = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/artifacts/${artifactId}`);
      setArtifact(resp.data);
      setEditName(resp.data.name);
      setEditContent(resp.data.content);
      setEditType(resp.data.type);
    } catch { toast.error(t("Artefact niet gevonden")); navigate(-1); }
    finally { setLoading(false); }
  }, [artifactId, navigate, t]);

  useEffect(() => {
    if (artifactId) fetchArtifact();
  }, [artifactId, fetchArtifact]);

  const handleUpdate = async () => {
    try {
      const resp = await axios.put(`${API}/artifacts/${artifactId}`, { name: editName, content: editContent, type: editType });
      setArtifact(resp.data);
      setEditing(false);
      toast.success(t("Artefact opgeslagen"));
    } catch { toast.error(t("Artefact opslaan mislukt")); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this artifact?")) return;
    try {
      await axios.delete(`${API}/artifacts/${artifactId}`);
      toast.success(t("Artefact verwijderd"));
      navigate(projectId ? `/project/${projectId}` : "/");
    } catch { toast.error(t("Artefact verwijderen mislukt")); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><p className="text-muted-foreground">{t("Laden...")}</p></div>;
  if (!artifact) return null;

  return (
    <div className={`flex flex-col h-screen ${BOTTOM_NAV_SPACER}`}>
      <header className="border-b px-4 sm:px-6 py-3 flex items-center gap-4 pt-[calc(0.75rem+var(--veilig-boven))]">
        <Button variant="ghost" size="icon" onClick={() => navigate(projectId ? `/project/${projectId}` : "/")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 flex items-center gap-3">
          {editing ? (
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="max-w-[400px]" />
          ) : (
            <h1 className="text-lg font-semibold">{artifact.name}</h1>
          )}
          <span className="text-xs uppercase text-muted-foreground bg-muted px-2 py-0.5 rounded">{editing ? editType : artifact.type}</span>
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <Button variant="ghost" size="icon" onClick={handleUpdate}><Check className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setEditing(false)}><X className="h-4 w-4" /></Button>
            </>
          ) : (
            <>
              {/* NS-A2: een document is een bestand; als tekst bewerken maakt het kapot. */}
              {!artifact.bijlage && (
                <Button variant="ghost" size="icon" onClick={() => setEditing(true)} data-testid="artefact-bewerk"><Edit3 className="h-4 w-4" /></Button>
              )}
              <Button variant="ghost" size="icon" className="text-destructive" onClick={handleDelete}><Trash2 className="h-4 w-4" /></Button>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 p-6 overflow-y-auto">
        {editing && (
          <div className="mb-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">{t("Soort:")}</label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">{t("Tekst")}</SelectItem>
                  <SelectItem value="markdown">Markdown</SelectItem>
                  <SelectItem value="code">{t("Code")}</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {artifact.bijlage ? (
          <BijlageKaart att={artifact.bijlage} />
        ) : editing ? (
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="min-h-[calc(100vh-200px)] font-mono text-sm"
          />
        ) : (
          <pre className="whitespace-pre-wrap font-mono text-sm bg-muted/30 rounded-lg p-4 min-h-[calc(100vh-200px)]">
            {artifact.content}
          </pre>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
}
